/**
 * The review before a copy is written (TF-SCHEDULE-COMPLETE). The server has
 * already mapped every source shift onto the destination and said which rows it
 * would create, which it would leave out and which it cannot map at all; this
 * shows exactly that, and the button says how many drafts it is about to make.
 * Nothing here writes: opening and cancelling send nothing.
 */
import type { ApiPlan, ApiPlanRow } from '@/api/data';
import { T } from '@/i18n';
import { Button, Drawer, Empty } from '@/ui';
import { NO_VALUE, fmtD } from '@/ui/format';
import { Icon } from '@/ui/icons';

const REASONS: Record<string, string> = {
  source_not_published: 'Kilden er ikke publisert',
  outside_target_window: 'Faller utenfor perioden',
  clock_does_not_exist: 'Klokkeslettet finnes ikke den dagen',
  clock_happens_twice: 'Klokkeslettet finnes to ganger den dagen',
  shift_type_missing: 'Vakttypen finnes ikke lenger',
  assignee_not_in_company: 'Personen er ikke i bedriften lenger',
  assignee_inactive: 'Personen er ikke aktiv',
  overlaps_existing: 'Overlapper en vakt som allerede finnes',
};

function Rows({ rows, testid }: { rows: ApiPlanRow[]; testid: string }) {
  if (!rows.length) return null;
  return (
    <div data-testid={testid}>
      {rows.map((r) => (
        <div className="ibrow" key={r.key} style={{ gridTemplateColumns: '1fr auto', padding: '6px 0' }}>
          <span className="ibt">
            <b>
              {r.holder ?? T('Ledig vakt')}
              {r.start_date ? `, ${fmtD(r.start_date)}` : ''}
              {r.start_clock ? ` ${r.start_clock}–${r.end_clock}` : ''}
              {r.overnight ? ` (${T('neste dag')})` : ''}
            </b>
            <span>
              {r.type_name ?? ''}
              {r.tasks ? `, ${T('{count} aktiviteter', { count: r.tasks })}` : ''}
              {r.reason ? `, ${T(REASONS[r.reason] ?? r.reason)}${r.at ? ` (${r.at})` : ''}` : ''}
            </span>
          </span>
          <span />
        </div>
      ))}
    </div>
  );
}

export function PlanDrawer({
  open,
  title,
  sub,
  plan,
  busy,
  info,
  problem,
  confirmLabel,
  onConfirm,
  onCheck,
  onClose,
  options,
}: {
  open: boolean;
  title: string;
  sub?: string | undefined;
  plan: ApiPlan | null;
  busy: boolean;
  /** what a status check found: said plainly, not as an error */
  info?: string | undefined;
  problem?: string | undefined;
  confirmLabel?: string | undefined;
  onConfirm: () => void;
  /** Present only while an outcome is unknown: reads what happened, never creates. */
  onCheck?: (() => void) | undefined;
  onClose: () => void;
  options?: React.ReactNode | undefined;
}) {
  const c = plan?.counts;
  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={title}
      sub={sub}
      footer={
        <div style={{ display: 'flex', gap: 8 }}>
          <span className="sp" />
          <Button onClick={onClose}>{T('Avbryt')}</Button>
          {onCheck ? (
            <Button disabled={busy} onClick={onCheck}>
              <Icon name="refresh" />
              {T('Sjekk status')}
            </Button>
          ) : (
            <Button tone="pri" disabled={busy || !c || c.ready === 0} onClick={onConfirm}>
              {confirmLabel ?? T('Kopier som utkast')}
              {c ? ` (${c.ready})` : ''}
            </Button>
          )}
        </div>
      }
    >
      {options}
      {info ? <p data-testid="plan-info">{info}</p> : null}
      {problem ? (
        <span role="alert" style={{ color: 'var(--dgr-i)' }}>
          {problem}
        </span>
      ) : null}
      {!plan ? (
        <p className="hint">{T('Leser kilden…')}</p>
      ) : (
        <>
          <div className="fld">
            <span className="lbl">{T('Fra')}</span>
            <span data-testid="plan-source">
              {plan.source ? `${fmtD(plan.source.start_date)} – ${fmtD(plan.source.end_date)}` : NO_VALUE}
              {plan.source?.found != null ? `, ${T('{count} vakter', { count: plan.source.found })}` : ''}
            </span>
          </div>
          <div className="fld">
            <span className="lbl">{T('Til')}</span>
            <span data-testid="plan-target">
              {fmtD(plan.target.start_date)} – {fmtD(plan.target.end_date)}, {plan.zone}
            </span>
          </div>
          <div className="fld" data-testid="plan-counts">
            <span className="lbl">{T('Dette blir laget')}</span>
            <span>
              {T('{count} vakter', { count: c?.ready ?? 0 })},{' '}
              {T('{count} med person', { count: c?.assigned ?? 0 })},{' '}
              {T('{count} ledige', { count: c?.open ?? 0 })},{' '}
              {T('{count} aktiviteter', { count: c?.tasks ?? 0 })}
            </span>
          </div>
          <div className="fld">
            <span className="lbl">{T('Finnes fra før i perioden')}</span>
            <span>{T('{count} vakter', { count: c?.existing_in_target ?? 0 })}</span>
          </div>
          {c && c.ready > 0 ? (
            <>
              <h4>{T('Klar')}</h4>
              <Rows rows={plan.ready} testid="plan-ready" />
            </>
          ) : (
            <Empty>{T('Ingenting å kopiere.')}</Empty>
          )}
          {c && c.blocked > 0 ? (
            <>
              <h4>{T('Må rettes')}</h4>
              <Rows rows={plan.blocked} testid="plan-blocked" />
            </>
          ) : null}
          {c && c.excluded > 0 ? (
            <>
              <h4>{T('Utelatt')}</h4>
              <Rows rows={plan.excluded} testid="plan-excluded" />
            </>
          ) : null}
        </>
      )}
    </Drawer>
  );
}
